import { useTranslation } from "react-i18next";
import type { McpCall } from "~/features/conversations/skills/parseSkills";
import { Text } from "~/foundation/components/Text";

/**
 * What `call_mcp_tool` actually did (W8 §B.6).
 *
 * It makes **no network call**. Every string below is documentation of a request the SERVER
 * already sent, written down so a reader can audit it — which is the entire point of the
 * panel and the reason the secrets line is not decoration: the browser never holds the API
 * key or the OAuth token, only the scheme, and a trace that showed `••••` without saying why
 * would read as redaction rather than as absence.
 *
 * Two disclosures, in the order a person needs them: what happened, then how.
 */
export function McpCallTrace({ call }: { call: McpCall }) {
  const { t } = useTranslation();
  const argEntries = Object.entries(call.args);
  const tool = call.tool || t("an unnamed tool");
  const server = call.serverId ?? t("default");

  return (
    <div className="flex flex-col gap-1.5 rounded-md border border-divider bg-bg-secondary p-2">
      <details open>
        <summary className="cursor-pointer list-none text-small text-text">
          <span aria-hidden className="me-1 inline-block rtl:rotate-180">
            ▸
          </span>
          {t("What this did")}
        </summary>
        <div className="mt-1 flex flex-col gap-1 ps-4">
          <Text variant="small" className="text-text-secondary">
            {argEntries.length > 0
              ? t(
                  "The assistant ran the {{tool}} tool on the {{server}} server with the following inputs:",
                  { tool, server },
                )
              : t("The assistant ran the {{tool}} tool on the {{server}} server with no inputs.", {
                  tool,
                  server,
                })}
          </Text>
          {argEntries.length > 0 ? (
            <dl className="flex flex-col gap-0.5">
              {argEntries.map(([key, value]) => (
                <div key={key} className="flex gap-2">
                  <dt className="text-caption text-text-tertiary">{key}</dt>
                  <dd className="min-w-0 flex-1 truncate text-caption text-text" dir="ltr">
                    {typeof value === "string" ? value : JSON.stringify(value)}
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      </details>

      <details>
        <summary className="cursor-pointer list-none text-small text-text-tertiary">
          <span aria-hidden className="me-1 inline-block rtl:rotate-180">
            ▸
          </span>
          {t("Technical details")}
        </summary>
        <dl className="mt-1 flex flex-col gap-0.5 ps-4">
          <Row label={t("Transport")} value={t("HTTP — MCP bridge via the tool registry")} />
          <Row label={t("Endpoint")} value={`POST /api/core/mcp/tools/${call.tool}/call`} />
          <Row label={t("Content type")} value="application/json" />
          <Row label={t("Authorization")} value="Bearer ••••••••" />
          <Row label={t("API key")} value="••••••••" />
        </dl>
        <div className="mt-1 ps-4">
          <Text variant="small" className="text-text-tertiary">
            {t("Request")}
          </Text>
          {/* LTR and monospaced whatever the page direction: this is a payload, not prose. */}
          <pre
            dir="ltr"
            className="scrollbar-thin mt-0.5 max-h-40 overflow-auto rounded-sm bg-bg p-2 text-caption text-text-secondary"
          >
            {call.raw}
          </pre>
          <Text variant="small" className="mt-1 text-text-tertiary">
            {t(
              "Secret values (API keys, OAuth tokens) are held server-side and are never sent to the browser — only the auth scheme is shown above.",
            )}
          </Text>
        </div>
      </details>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-caption text-text-tertiary">{label}</dt>
      <dd className="min-w-0 flex-1 truncate text-caption text-text" dir="ltr">
        {value}
      </dd>
    </div>
  );
}
